import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';


const LEVELS = ['overview', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5'];

const CompletionBadge = ({ rate }) => {
  if (rate == null) return <span className="text-white/20 text-xs">—</span>;
  const color =
    rate >= 75 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
    rate >= 50 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30': 'bg-red-500/20 text-red-300 border-red-500/30';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${color}`}>
      {rate}%
    </span>
  );
};

const SongLevelTable = ({ data, loading }) => {
  const [expanded, setExpanded] = useState({});

  const toggle = (songId) => setExpanded(prev => ({ ...prev, [songId]: !prev?.[songId] }));

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-white/10">
          <div className="h-5 w-48 bg-white/10 rounded animate-pulse" />
        </div>
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="p-4 border-b border-white/5 flex gap-4">
            <div className="h-4 w-40 bg-white/10 rounded animate-pulse" />
            <div className="h-4 w-16 bg-white/10 rounded animate-pulse ml-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
        <p className="text-white/40 text-sm">No song view data available for this period.</p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-5 border-b border-white/10">
        <h3 className="text-white font-semibold text-base">Views & Completion by Song</h3>
        <p className="text-white/40 text-xs mt-0.5">Click a row to expand level breakdown</p>
      </div>
      {/* Header */}
      <div className="grid grid-cols-12 gap-2 px-5 py-2.5 bg-white/5 border-b border-white/10">
        <div className="col-span-5 text-xs font-semibold text-white/40 uppercase tracking-wider">Song</div>
        <div className="col-span-2 text-xs font-semibold text-white/40 uppercase tracking-wider text-right">Total Views</div>
        <div className="col-span-3 text-xs font-semibold text-white/40 uppercase tracking-wider text-right">Completion Rate</div>
        <div className="col-span-2" />
      </div>
      <div className="divide-y divide-white/5">
        {data?.map(song => (
          <div key={song?.songId}>
            {/* Song row */}
            <button
              onClick={() => toggle(song?.songId)}
              className="w-full grid grid-cols-12 gap-2 px-5 py-3.5 hover:bg-white/5 transition-colors text-left"
            >
              <div className="col-span-5 flex items-center gap-2">
                {expanded?.[song?.songId]
                  ? <ChevronDown className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                }
                <span className="text-white text-sm font-medium truncate">{song?.songTitle}</span>
              </div>
              <div className="col-span-2 text-right">
                <span className="text-white/70 text-sm font-mono">{song?.totalViews?.toLocaleString()}</span>
              </div>
              <div className="col-span-3 flex justify-end">
                <CompletionBadge rate={song?.overallCompletionRate} />
              </div>
              <div className="col-span-2" />
            </button>

            {/* Level breakdown */}
            {expanded?.[song?.songId] && (
              <div className="bg-white/[0.03] border-t border-white/5">
                <div className="grid grid-cols-12 gap-2 px-5 py-2 border-b border-white/5">
                  <div className="col-span-3 col-start-2 text-xs text-white/30 font-medium">Level</div>
                  <div className="col-span-2 text-xs text-white/30 font-medium text-right">Views</div>
                  <div className="col-span-2 text-xs text-white/30 font-medium text-right">Completion</div>
                  <div className="col-span-2 text-xs text-white/30 font-medium text-right">Rewatches</div>
                </div>
                {song?.levelBreakdown?.map(lvl => (
                  <div key={lvl?.level} className="grid grid-cols-12 gap-2 px-5 py-2.5 hover:bg-white/5 transition-colors">
                    <div className="col-span-3 col-start-2">
                      <span className="text-xs text-white/60 font-medium">{lvl?.label}</span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-xs text-white/50 font-mono">{lvl?.views > 0 ? lvl?.views : '—'}</span>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <CompletionBadge rate={lvl?.completionRate} />
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-xs text-white/50 font-mono">{lvl?.rewatches > 0 ? lvl?.rewatches : '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SongLevelTable;
